// packages/web/src/components/AdminOverview.tsx
import React, { useState, useEffect } from 'react'
import {
  ShoppingBag,
  TrendingUp,
  Clock,
  AlertTriangle,
  DollarSign,
  ArrowUpRight,
  Package,
  ShieldCheck,
  Zap,
  Download,
} from 'lucide-react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { apiGet } from '../lib/api'
import { useSiteConfig } from '../context/SiteConfigContext'
import { AppDataTable } from './AppDataTable'

interface DashboardStats {
  totalOrders: number
  totalRevenue: number
  pendingOrders: number
  lowStockAlerts: number
  mtdRevenue: number
}

interface RevenueChartItem {
  date: string
  orders: number
  revenue: number
}

interface CategoryBreakdownItem {
  category: string
  order_count: number
  revenue: number
}

interface RecentOrder {
  id: string
  order_number: string
  customer_name: string
  customer_phone: string
  status: string
  total: number
  created_at: string
  tracking_id: string
}

const DEFAULT_STATS: DashboardStats = {
  totalOrders: 0,
  totalRevenue: 0,
  pendingOrders: 0,
  lowStockAlerts: 0,
  mtdRevenue: 0,
}

export const AdminOverview: React.FC = () => {
  const { config } = useSiteConfig()
  const [stats, setStats] = useState<DashboardStats>(DEFAULT_STATS)
  const [chartData, setChartData] = useState<RevenueChartItem[]>([])
  const [categories, setCategories] = useState<CategoryBreakdownItem[]>([])
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      apiGet<DashboardStats>('dashboard/stats'),
      apiGet<RevenueChartItem[]>('dashboard/revenue-chart'),
      apiGet<CategoryBreakdownItem[]>('dashboard/category-breakdown'),
      apiGet<RecentOrder[]>('dashboard/recent-orders'),
    ])
      .then(([statsRes, chartRes, catRes, ordersRes]) => {
        if (statsRes) setStats(statsRes)
        if (chartRes) setChartData(chartRes)
        if (catRes) setCategories(catRes)
        if (ordersRes) setRecentOrders(ordersRes)
      })
      .catch((err) => console.error('Dashboard API error:', err))
      .finally(() => setLoading(false))
  }, [])

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)

  const recentOrderColumns = [
    {
      name: 'Order No',
      selector: (ord: RecentOrder) => (
        <span className="font-mono font-bold text-[var(--gold-main)]">{ord.order_number}</span>
      ),
    },
    {
      name: 'Customer',
      selector: (ord: RecentOrder) => (
        <span className="font-medium text-[var(--text-primary)]">{ord.customer_name}</span>
      ),
    },
    {
      name: 'Phone',
      selector: (ord: RecentOrder) => (
        <span className="font-mono">{ord.customer_phone}</span>
      ),
    },
    {
      name: 'Status',
      cell: (ord: RecentOrder) => (
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
            ord.status === 'delivered'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
              : ord.status === 'shipped'
              ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
              : ord.status === 'processing'
              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
              : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
          }`}
        >
          {ord.status}
        </span>
      ),
    },
    {
      name: 'Total Amount',
      selector: (ord: RecentOrder) => (
        <span className="font-title font-bold gold-shimmer-text">{formatCurrency(ord.total)}</span>
      ),
    },
    {
      name: 'Tracking ID',
      selector: (ord: RecentOrder) => (
        <span className="font-mono text-[11px]">{ord.tracking_id || 'Pending'}</span>
      ),
    },
    {
      name: 'Date',
      selector: (ord: RecentOrder) => (
        <span className="text-[11px] text-[var(--text-faint)]">
          {new Date(ord.created_at).toLocaleDateString('en-IN')}
        </span>
      ),
    },
  ]

  const COLORS = [
    'var(--gold-main)',
    'var(--chart-accent-2, #60a5fa)',
    'var(--chart-accent-3, #a78bfa)',
    'var(--chart-accent-4, #34d399)',
    'var(--chart-accent-5, #f472b6)',
    'var(--chart-accent-6, #fbbf24)',
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-[var(--bg-surface-2)] rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-72 bg-[var(--bg-surface-2)] rounded-2xl animate-pulse" />
      </div>
    )
  }

  const reportDate = new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div className="dashboard-print-report space-y-8 animate-fade-in-up">
      {/* Print-only report header */}
      <div className="dashboard-print-header hidden print:block">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8f6d3d] font-mono">Business Report</p>
        <h1 className="text-2xl font-title font-bold text-[#1a1a1a] mt-1">
          {config.brand_name || "Buddy's Choice"} — Executive Dashboard
        </h1>
        <p className="text-xs text-[#666] mt-1">Generated on {reportDate}</p>
      </div>

      {/* Top Banner Status Bar */}
      <div className="glass-luxury-card admin-print-hidden p-4 sm:p-6 border-[var(--gold-main)]/30 bg-gradient-to-r from-[var(--bg-main)] via-[var(--bg-surface-2)] to-[var(--bg-main)] flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
              <h2 className="text-lg sm:text-2xl font-title font-bold text-[var(--text-primary)] uppercase tracking-wider break-words">
                Executive Dashboard
              </h2>
              <span className="admin-print-hidden badge bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 text-[10px] w-fit shrink-0">
                ● LIVE D1 CONNECTED
              </span>
            </div>
            <p className="text-xs text-[var(--text-muted)] font-light mt-1.5 leading-relaxed">
              Real-time analytics, revenue forecasting, inventory thresholds, and customer orders.
            </p>
          </div>

          <button
            onClick={() => window.print()}
            className="admin-print-hidden btn-gold-outline text-xs py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 w-full sm:w-auto shrink-0 min-h-[44px]"
          >
            <Download className="w-3.5 h-3.5 text-[var(--gold-main)]" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-luxury-card p-4 sm:p-5 border-[var(--gold-main)]/20 relative overflow-hidden bg-[var(--bg-surface)]">
          <div className="flex items-center justify-between text-xs text-[var(--text-faint)] uppercase tracking-wider font-mono">
            <span>TOTAL REVENUE</span>
            <div className="w-8 h-8 rounded-xl bg-[var(--gold-main)]/15 text-[var(--gold-main)] border border-[var(--gold-main)]/30 flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-title font-bold gold-shimmer-text mt-2">
            {formatCurrency(stats.totalRevenue)}
          </div>
          <p className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1 font-medium font-mono">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>MTD: {formatCurrency(stats.mtdRevenue)}</span>
          </p>
        </div>

        <div className="glass-luxury-card p-4 sm:p-5 border-blue-500/20 relative overflow-hidden bg-[var(--bg-surface)]">
          <div className="flex items-center justify-between text-xs text-[var(--text-faint)] uppercase tracking-wider font-mono">
            <span>Total Orders</span>
            <div className="w-8 h-8 rounded-xl bg-blue-500/15 text-blue-400 border border-blue-500/30 flex items-center justify-center">
              <ShoppingBag className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-title font-bold text-[var(--text-primary)] mt-2">{stats.totalOrders}</div>
          <p className="text-[11px] text-[var(--text-faint)] mt-1.5 font-mono">Processed across store catalog</p>
        </div>

        <div className="glass-luxury-card p-4 sm:p-5 border-amber-500/20 relative overflow-hidden bg-[var(--bg-surface)]">
          <div className="flex items-center justify-between text-xs text-[var(--text-faint)] uppercase tracking-wider font-mono">
            <span>Pending Dispatch</span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-title font-bold text-amber-400 mt-2">{stats.pendingOrders}</div>
          <p className="text-[11px] text-amber-400/80 mt-1.5 font-mono">Action required by fulfillment team</p>
        </div>

        <div className="glass-luxury-card p-4 sm:p-5 border-red-500/20 relative overflow-hidden bg-[var(--bg-surface)]">
          <div className="flex items-center justify-between text-xs text-[var(--text-faint)] uppercase tracking-wider font-mono">
            <span>Low Stock Alerts</span>
            <div className="w-8 h-8 rounded-xl bg-red-500/15 text-red-400 border border-red-500/30 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-title font-bold text-red-400 mt-2">{stats.lowStockAlerts}</div>
          <p className="text-[11px] text-red-400/80 mt-1.5 font-mono">Variants below threshold limit (&lt; 5)</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend Area Chart */}
        <div className="lg:col-span-2 glass-luxury-card p-4 sm:p-6 border-[var(--gold-main)]/20 bg-[var(--bg-surface)] min-w-0">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4 pb-3 border-b border-[var(--border)]">
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-title font-bold text-[var(--text-primary)]">
                Revenue Performance (30 Days)
              </h3>
              <p className="text-xs text-[var(--text-faint)]">Daily revenue from paid customer orders in INR</p>
            </div>
            <TrendingUp className="w-5 h-5 text-[var(--gold-main)] shrink-0 hidden sm:block" />
          </div>

          <div className="h-48 sm:h-64 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--gold-main)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--gold-main)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="var(--text-primary)" opacity={0.4} tick={{ fontSize: 10 }} />
                <YAxis stroke="var(--text-primary)" opacity={0.4} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-main)',
                    borderColor: 'var(--gold-main)',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: 'var(--text-primary)',
                  }}
                  formatter={(val: any) => [`₹${Number(val || 0).toLocaleString('en-IN')}`, 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--gold-main)"
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#goldGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Revenue Breakdown */}
        <div className="glass-luxury-card p-4 sm:p-6 border-[var(--gold-main)]/20 bg-[var(--bg-surface)] flex flex-col justify-between min-w-0">
          <div className="pb-3 border-b border-[var(--border)]">
            <h3 className="text-base font-title font-bold text-[var(--text-primary)]">Category Distribution</h3>
            <p className="text-xs text-[var(--text-faint)]">Sales breakdown by jewellery type</p>
          </div>

          <div className="h-48 my-2 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categories}
                  dataKey="revenue"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  paddingAngle={4}
                >
                  {categories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-main)',
                    border: '1px solid var(--gold-main)',
                    borderRadius: '8px',
                    fontSize: '11px',
                    color: 'var(--text-primary)',
                  }}
                  formatter={(val: any) => [`₹${Number(val || 0).toLocaleString('en-IN')}`, 'Revenue']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2 text-xs">
            {categories.map((cat, idx) => (
              <div key={cat.category} className="flex items-center justify-between text-[var(--text-muted)]">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                  <span className="capitalize font-medium text-[var(--text-primary)]">{cat.category}</span>
                </div>
                <span className="font-mono text-[var(--gold-main)] font-semibold">{formatCurrency(cat.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Orders Table */}
      <div className="glass-luxury-card p-4 sm:p-6 border-[var(--gold-main)]/20 bg-[var(--bg-surface)] space-y-4 min-w-0">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-3 border-b border-[var(--border)]">
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-title font-bold text-[var(--text-primary)]">
              Recent Customer Orders
            </h3>
            <p className="text-xs text-[var(--text-faint)]">Live stream of store checkout transactions</p>
          </div>
          <span className="badge bg-[var(--gold-main)]/10 text-[var(--gold-main)] border border-[var(--gold-main)]/30 text-[10px] w-fit shrink-0">
            {recentOrders.length} Recent
          </span>
        </div>

        {/* Mobile: card list */}
        <div className="md:hidden print:hidden space-y-3">
          {recentOrders.map((ord) => (
            <div
              key={ord.id}
              className="rounded-xl border border-[var(--border)] bg-[var(--bg-main)]/50 p-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono font-bold text-[var(--gold-main)] text-xs">{ord.order_number}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase shrink-0 ${
                    ord.status === 'delivered'
                      ? 'bg-emerald-500/20 text-emerald-600 border border-emerald-500/30'
                      : ord.status === 'shipped'
                      ? 'bg-blue-500/20 text-blue-600 border border-blue-500/30'
                      : 'bg-amber-500/20 text-amber-600 border border-amber-500/30'
                  }`}
                >
                  {ord.status}
                </span>
              </div>
              <p className="text-sm font-medium text-[var(--text-primary)]">{ord.customer_name}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--text-muted)]">
                <span>{ord.customer_phone}</span>
                <span className="font-title font-bold gold-shimmer-text">{formatCurrency(ord.total)}</span>
              </div>
              <p className="text-[10px] text-[var(--text-faint)] font-mono truncate">
                {ord.tracking_id || 'Pending'} · {new Date(ord.created_at).toLocaleDateString('en-IN')}
              </p>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block print:block rounded-xl border border-[var(--border)] overflow-hidden">
          <AppDataTable
            columns={recentOrderColumns}
            data={recentOrders}
            keyField={(ord) => ord.id}
            noDataComponent="No recent orders."
          />
        </div>
      </div>
    </div>
  )
}
